import { Metadata } from 'next';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { AccessScreen } from '../../components/Guides/AccessScreen';
import { BookContent } from '../../components/Guides/BookContent';
import { BookSidebar } from '../../components/Guides/BookSidebar';
import { CommunityWrapper } from '../../components/Layouts/CommunityWrapper';
import { getGuidebookBySlug, getGuidebooks } from '../../lib/contentful';
import { Book, GuideBook } from '../../lib/guideBooks';
import { useAuthStore } from '../../store/AuthStore';
import { withOnboardingRequired } from '../../util/withOnboardingRequired';

type Props = {
  book: Book;
};

export const GuidebookPage = ({ book }: Props) => {
  const { title, contentCollection } = book;
  const [isLoaded, setIsLoaded] = useState(false);
  const { isCommunityUser } = useAuthStore(({ state }) => state);

  // Needed because Next will throw a rehydration error
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <CommunityWrapper className="guide-book-page">
      <Head>
        <title>{book.title}</title>
      </Head>

      {isLoaded && !isCommunityUser && <AccessScreen book={book} />}

      {isLoaded && isCommunityUser && (
        <div className="container" id="scroll-container">
          <div className="grid-layout my-10">
            <div className="col content flex flex-col">
              <BookContent book={book} />
            </div>

            <div className="col sidebar">
              <BookSidebar
                bookTitle={title}
                chapters={contentCollection.items}
              />
            </div>
          </div>
        </div>
      )}
    </CommunityWrapper>
  );
};

export async function getStaticPaths() {
  const books = await getGuidebooks();

  return {
    paths: books?.map(({ slug }) => `/guides/${slug}`) ?? [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }) {
  const res = await getGuidebookBySlug(params.slug);

  const book = new GuideBook(res);
  await book.init();
  const bookExport = book.export();

  const metadata: Metadata = {
    openGraph: {
      title: bookExport.title,
      description: `${bookExport.contentCollection?.items
        ?.map((i) => i.displayTitle)
        .join(' \n ')}`,
      images: bookExport.cover?.url,
    },
  };

  return {
    props: {
      book: bookExport ?? null,
      metadata,
    },
    revalidate: 1,
  };
}

export default withOnboardingRequired(GuidebookPage);
